import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/cn';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'subtle';
export type ButtonSize = 'sm' | 'md';

const variants: Record<ButtonVariant, string> = {
  primary: 'bg-accent text-accent-ink hover:bg-accent-hover border border-accent',
  secondary: 'bg-surface text-ink border border-line-strong hover:bg-surface-2',
  ghost: 'bg-transparent text-ink border border-transparent hover:bg-surface-2',
  subtle: 'bg-surface-2 text-ink border border-line hover:bg-surface-3',
  danger: 'bg-danger text-white border border-danger hover:opacity-90',
};

/** md = 36px desktop / 44px touch target below 640px (DESIGN.md §4); sm = 32px compact (tables, toolbars). */
const sizes: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-[13px] gap-1.5',
  md: 'h-9 px-4 text-sm gap-2 max-sm:min-h-11',
};

const base =
  'inline-flex shrink-0 items-center justify-center rounded-md font-medium whitespace-nowrap select-none ' +
  'transition-[background-color,border-color,color,opacity,transform] duration-150 ease-out active:translate-y-px ' +
  '[&_svg]:shrink-0';

/** Button look for links (`<Link className={buttonClass('primary')}>`) — avoids nesting <button> in <a>. */
export function buttonClass(variant: ButtonVariant = 'secondary', size: ButtonSize = 'md', className?: string) {
  return cn(
    base,
    variants[variant],
    sizes[size],
    className,
  );
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  icon?: ReactNode;
  iconRight?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', loading, icon, iconRight, className, children, disabled, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        base,
        'disabled:cursor-not-allowed disabled:opacity-55 disabled:active:translate-y-0',
        variants[variant],
        sizes[size],
        className,
      )}
      {...rest}
    >
      {loading ? <Loader2 className="size-4 animate-spin" aria-hidden /> : icon}
      {children}
      {iconRight}
    </button>
  );
});

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Required: icon-only buttons need an accessible name. */
  label: string;
  variant?: 'ghost' | 'secondary' | 'subtle';
  size?: 'sm' | 'md';
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, variant = 'ghost', size = 'md', className, children, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={label}
      className={cn(
        'relative inline-flex shrink-0 items-center justify-center rounded-md text-muted transition-colors duration-150 hover:text-ink active:translate-y-px',
        'disabled:cursor-not-allowed disabled:opacity-50 disabled:active:translate-y-0',
        // 44px hit area on touch screens without changing the visual size (rows stay dense).
        'max-sm:after:absolute',
        variant === 'ghost' && 'hover:bg-surface-2',
        variant === 'secondary' && 'border border-line-strong bg-surface hover:bg-surface-2',
        variant === 'subtle' && 'bg-surface-2 hover:bg-surface-3',
        size === 'sm' ? 'size-7 max-sm:after:-inset-2' : 'size-9 max-sm:size-10 max-sm:after:-inset-0.5',
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
});
